import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Percent } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, money, shortDate } from "@/lib/api";
import { Card, PageHeader, StatusBadge, EmptyState, Btn } from "@/components/admin/ui";

export const describe = (d) => {
  const what = d.type === "percentage" ? `${d.value}% off` : d.type === "fixed" ? `${money(d.value)} off` : "Free shipping";
  const scope = d.applies_to === "products" ? ` ${d.product_ids.length} product${d.product_ids.length === 1 ? "" : "s"}` : " all products";
  const min = d.min_requirement === "amount" ? ` · Min. ${money(d.min_amount)}` : d.min_requirement === "quantity" ? ` · Min. ${d.min_quantity} items` : "";
  return `${what} on${scope}${min}`;
};

export default function Discounts() {
  const [list, setList] = useState(null);
  const nav = useNavigate();
  const load = useCallback(() => api.get("/admin/discounts").then((r) => setList(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (d) => {
    try {
      await api.patch(`/admin/discounts/${d.id}/active`, { active: !d.active });
      toast.success(d.active ? "Discount turned off" : "Discount turned on");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-discounts-page">
      <PageHeader title="Discounts" subtitle="Create discount codes and automatic offers. Only one discount applies per order.">
        <Link to="/admin/discounts/new"><Btn data-testid="discounts-create-button"><Plus size={16} /> Create discount</Btn></Link>
      </PageHeader>
      <Card className="p-0 sm:p-0 overflow-hidden">
        {list === null ? (
          <div className="py-14 text-center text-sm text-zinc-400" data-testid="discounts-loading">Loading discounts…</div>
        ) : list.length === 0 ? (
          <EmptyState icon={Percent} title="No discounts yet" text="Create a code like SPICY10, or an automatic offer like free shipping on 3+ items." testid="discounts-empty">
            <Link to="/admin/discounts/new"><Btn data-testid="discounts-empty-create-button"><Plus size={16} /> Create discount</Btn></Link>
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="discounts-table">
              <thead className="bg-zinc-50 text-zinc-500 text-xs">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Discount</th>
                  <th className="text-left font-medium px-4 py-2.5">Status</th>
                  <th className="text-left font-medium px-4 py-2.5">Method</th>
                  <th className="text-left font-medium px-4 py-2.5">Dates</th>
                  <th className="text-right font-medium px-4 py-2.5">Used</th>
                  <th className="text-right font-medium px-4 py-2.5">On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(list || []).map((d) => (
                  <tr key={d.id} className="hover:bg-zinc-50" data-testid={`discount-row-${d.code || d.id}`}>
                    <td className="px-4 py-3 cursor-pointer" onClick={() => nav(`/admin/discounts/${d.id}`)}>
                      <p className="font-semibold text-zinc-950 hover:underline">{d.code || d.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{describe(d)}</p>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={d.status} testid={`discount-status-${d.code || d.id}`} /></td>
                    <td className="px-4 py-3 text-zinc-600">{d.method === "code" ? "Code" : "Automatic"}</td>
                    <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{shortDate(d.starts_at)}{d.ends_at ? ` – ${shortDate(d.ends_at)}` : " – no end"}</td>
                    <td className="px-4 py-3 text-right">{d.times_used || 0}{d.usage_limit ? ` / ${d.usage_limit}` : ""}</td>
                    <td className="px-4 py-3 text-right"><Switch checked={d.active} onCheckedChange={() => toggle(d)} data-testid={`discount-active-toggle-${d.code || d.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
